import React, { useEffect, useState } from 'react'
import { initReactI18next } from 'react-i18next'
import { resources } from './i18n'
import {
  Location,
  Route,
  Routes,
  matchPath,
  useLocation,
  useParams,
} from 'react-router-dom'
import ToastContainer from './components/toast/ToastContainer'
import Loader from './components/loader/Loader'
import { useGetInfoQuery } from './store/api/generalInfoApi'
import Home from './pages/home/Home'
import i18next from 'i18next'
import MainLayout from './layouts/MainLayout/MainLayout'
import InfoLayout from './layouts/InfoLayout/InfoLayout'
import SilderLayout from './layouts/SilderLayout/SilderLayout'
import CartPage from './pages/cart-page/CartPage'
import CheckoutPage from './pages/checkout-page/CheckoutPage'
import ProductModal from './components/product-modal/ProductModal'
import AdminLayout from './layouts/AdminLayout/AdminLayout'
import ProductsPage from './pages/admin/pages/products-page/ProductsPage'
import CategoriesPage from './pages/admin/pages/categories-page/CategoriesPage'
import LocationsPage from './pages/admin/pages/locations-page/LocationsPage'
import LoginModal from './components/login-modal/LoginModal'
import GeneralPage from './pages/admin/pages/general-page/GeneralPage'
import DeliveryConditionsPage from './pages/delivery-conditions/DeliveryConditionsPage'
import IngredientsPage from './pages/admin/pages/ingredients-page/IngredientsPage'
import ProductsCostLayout from './layouts/ProductsCostLayout/ProductsCostLayout'
import ProductsCostPage from './pages/admin/pages/products-cost-page/ProductsCostPage'
import OrdersPage from './pages/admin/pages/orders-page/OrdersPage'
import StatisticsPage from './pages/admin/pages/statistics-page/StatisticsPage'
import RoulettePage from './pages/admin/pages/roulette-page/RoulettePage'
import { IProduct } from './types/products'
import LandingPage from './pages/landing/LandingPage'
import { AppProvider, useAppType } from './context/AppContext'
import { getStorageKey, AppType } from './utils/appType'
import { useGetAppStatusQuery } from './store/api/appStatusApi'
import MaintenancePage from './pages/maintenance/MaintenancePage'
import { ProductRouteState } from './utils/openProduct'

const AppStatusGuard = ({ children }: { children: React.ReactNode }) => {
  const { data: appStatus, isLoading } = useGetAppStatusQuery()
  const { pathname } = useLocation()

  // Admin panel and login are always accessible
  const isAdminRoute =
    pathname.includes('/admin') || pathname.endsWith('/login')

  if (isLoading) {
    return <Loader fullscreen />
  }

  if (appStatus?.isMaintenanceMode && !isAdminRoute) {
    return <MaintenancePage />
  }

  const isSushiRoute = pathname.startsWith('/sushi')
  const isCateringRoute = pathname.startsWith('/catering')

  if (appStatus?.isSushiDisabled && isSushiRoute && !isAdminRoute) {
    return <MaintenancePage />
  }

  if (appStatus?.isCateringDisabled && isCateringRoute && !isAdminRoute) {
    return <MaintenancePage />
  }

  return <>{children}</>
}

const AppShell = () => {
  const { appType } = useParams<{ appType: string }>()
  const validAppType: AppType = appType === 'catering' ? 'catering' : 'sushi'

  return (
    <AppProvider appType={validAppType}>
      <AppContent key={appType} />
    </AppProvider>
  )
}

const AppContent = () => {
  const { data: generalInfo, isLoading } = useGetInfoQuery()
  const location = useLocation()
  const appType = useAppType()
  const productId =
    matchPath('/:appType/product/:id', location.pathname)?.params.id ?? null
  const background: Location | undefined =
    (location.state as ProductRouteState | null)?.backgroundLocation ??
    (productId
      ? { ...location, pathname: `/${appType}`, state: null }
      : undefined)

  const [cart, setCart] = useState<IProduct[]>(
    JSON.parse(localStorage.getItem(getStorageKey('cart')) || '[]'),
  )

  useEffect(() => {
    if (generalInfo) {
      localStorage.setItem(getStorageKey('info'), JSON.stringify(generalInfo))
    }
  }, [generalInfo])

  useEffect(() => {
    localStorage.setItem(getStorageKey('cart'), JSON.stringify(cart))
  }, [cart])

  if (isLoading) {
    return <Loader fullscreen />
  }

  return (
    <>
      <Routes location={background ?? location}>
        <Route path='/' element={<MainLayout cart={cart} />}>
          <Route
            path=''
            element={<SilderLayout cart={cart} setCart={setCart} />}
          >
            <Route path='' element={<Home />} />
            <Route
              path='delivery-conditions'
              element={<DeliveryConditionsPage />}
            />
          </Route>
          <Route
            path='cart'
            element={<InfoLayout cart={cart} setCart={setCart} />}
          >
            <Route path='' element={<CartPage />} />
            <Route path='checkout' element={<CheckoutPage />} />
          </Route>
        </Route>
        <Route path='admin' element={<AdminLayout />}>
          <Route path='general' element={<GeneralPage />} />
          <Route path='products' element={<ProductsPage />} />
          <Route path='categories' element={<CategoriesPage />} />
          <Route path='locations' element={<LocationsPage />} />
          <Route path='ingredients' element={<IngredientsPage />} />
          <Route path='costs' element={<ProductsCostLayout />}>
            <Route path=':id' element={<ProductsCostPage />} />
          </Route>
          <Route path='orders' element={<OrdersPage />} />
          <Route path='statistics' element={<StatisticsPage />} />
          <Route path='roulette' element={<RoulettePage />} />
        </Route>
        <Route path='login' element={<LoginModal isOpen />} />
      </Routes>
      <ProductModal id={productId} setCart={setCart} />
    </>
  )
}

let greeted = false

function greetDevelopers() {
  if (greeted) return
  greeted = true
  console.log(
    `%c
      ▄▀▀▄   ▄▀▀▄
      █  ▀▀▀▀▀  █
     █  ▄█▄ ▄█▄  █     Psst, curious one!
     █   ▀  ▼ ▀  █     Try the Konami code ↑↑↓↓←→←→BA
      ▀▄  ╰─╯  ▄▀      …or tap the logo 7 times
        ▀▀▀▀▀▀▀
`,
    'font-family: monospace; color: #ff1647; font-weight: bold',
  )
}

const App = () => {
  useEffect(greetDevelopers, [])
  const [isI18nInitialized, setIsI18nInitialized] = useState(false)

  useEffect(() => {
    i18next
      .use(initReactI18next)
      .init({
        resources,
        lng: localStorage.getItem('language') ?? 'ro',
        fallbackLng: 'ro',
        interpolation: {
          escapeValue: false,
        },
      })
      .then(() => setIsI18nInitialized(true))
  }, [])

  if (!isI18nInitialized) {
    return null
  }

  return (
    <div className='full-height'>
      <ToastContainer />
      <AppStatusGuard>
        <Routes>
          <Route path='/' element={<LandingPage />} />
          <Route path='/:appType/*' element={<AppShell />} />
        </Routes>
      </AppStatusGuard>
    </div>
  )
}

export default App
